import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from 'react-flexbox-grid';
import { map, defaultTo, descend, sortWith, path, compose } from 'ramda';

import HistoryContainer from 'containers/HistoryContainer';
import HistoryEntry from 'components/HistoryEntry';
import HistoryEntryHeader from 'components/HistoryEntryHeader';
import HistoryEntryColumns from 'components/HistoryEntryColumns';
import HistoryEntryColumn from 'components/HistoryEntryColumn';
import Paper from 'components/Paper';
import BottomFormControls from 'routes/Documents/components/BottomFormControls';

import CommonHeader from 'routes/Documents/components/CommonHeader';
import CommonHistoryEntryControls from 'routes/Documents/components/CommonHistoryEntryControls';
import CommonViewButton from 'routes/Documents/components/CommonViewButton';

import { formatHeaderDate, dateValue } from 'lib/dateDisplay';

import Form from './Form';
import { getLatestUnfinishedCommit, getLatestFinishedCommitData } from 'lib/documentHandling';
import EditableV2 from 'routes/Documents/containers/EditableV2';
import AlertDialog from 'components/AlertDialog';
import { FormattedMessage } from 'react-intl';
import { dateFromPartial, isPartialDate } from 'neuro-utils';

const sorter = sortWith([
  descend(compose(dateValue, path(['content', 'relapseDate']))),
  descend(compose(dateValue, path(['lastCommitTime']))),
]);

const optionAccessor = opt => `relapse.opts.${opt}`;
const localizeValue = (fm, value, opt) => (value ? fm({ id: opt ? opt(value) : optionAccessor(value) }) : '');

const historyMapper = (name, edit, view) => (d, intl) => (
  <HistoryEntry key={d.documentId}>
    <HistoryEntryHeader
      title={formatHeaderDate(
        isPartialDate(d.content.relapseDate) ? dateFromPartial(d.content.relapseDate) : d.content.relapseDate,
      )}
      active
    >
      {window.__ROLE__ !== 'DOCTOR' && (
        <CommonHistoryEntryControls action={() => edit(name, d.documentId, d.content)} />
      )}
      {window.__ROLE__ === 'DOCTOR' && <CommonViewButton onView={e => view('relapse', d.documentId)} />}
    </HistoryEntryHeader>
    <HistoryEntryColumns>
      <HistoryEntryColumn header={intl.formatMessage({ id: 'relapse.labels.symptoms' })}>
        {map(d => <p key={d} children={localizeValue(intl.formatMessage, d)} />)(
          defaultTo([])(d.content.relapseSymptoms),
        )}
      </HistoryEntryColumn>
      <HistoryEntryColumn header={intl.formatMessage({ id: 'relapse.labels.symptomDetails' })}>
        <p>{d.content.symptomDetails}</p>
      </HistoryEntryColumn>
      <HistoryEntryColumn header={intl.formatMessage({ id: 'relapse.labels.acuteSymptoms' })}>
        <p>{localizeValue(intl.formatMessage, d.content.acuteSymptoms, opt => `dictionary.${opt}`)}</p>
      </HistoryEntryColumn>
      <HistoryEntryColumn header={intl.formatMessage({ id: 'relapse.labels.feverOrInfection' })}>
        <p>{localizeValue(intl.formatMessage, d.content.feverOrInfection, opt => `dictionary.${opt}`)}</p>
      </HistoryEntryColumn>
      <HistoryEntryColumn header={intl.formatMessage({ id: 'relapse.labels.lessThanOneMonthSinceLastRelapse' })}>
        <p>
          {localizeValue(intl.formatMessage, d.content.lessThanOneMonthSinceLastRelapse, opt => `dictionary.${opt}`)}
        </p>
      </HistoryEntryColumn>
    </HistoryEntryColumns>
  </HistoryEntry>
);

class Relapse extends React.Component {
  state = {
    alertVisible: false,
  };

  openDialog = () => this.setState({ alertVisible: true });
  closeDialog = () => this.setState({ alertVisible: false });

  componentDidUpdate(prevProps) {
    if (prevProps.editingDocument && !this.props.editingDocument) {
      this.openDialog();
    }
  }

  render() {
    const { names, documents, editingDocument, viewId, onView, onStartEdit, onCreate } = this.props;
    return (
      <div>
        <Paper topMargin={false}>
          <Grid fluid>
            <CommonHeader
              names={names}
              localeId="relapse.title"
              infoText={editingDocument ? 'relapse.infoTextEditing' : 'relapse.infoText'}
              addNewText="relapse.addNew"
              mode="create"
              editing={editingDocument || (viewId ? { viewId } : undefined)}
              startEdit={onCreate}
              widerInfoTextArea
            />
          </Grid>

          {editingDocument && (
            <EditableV2
              documentId={editingDocument.documentId}
              moduleName={names[0]}
              editedCommit={getLatestUnfinishedCommit(editingDocument)}
            >
              <Form />
            </EditableV2>
          )}

          {!editingDocument && viewId && (
            <Form
              editing={false}
              formValues={getLatestFinishedCommitData(documents.find(d => d.documentId === viewId.id))}
            />
          )}

          {!editingDocument && !viewId && (
            <HistoryContainer
              documents={documents}
              emptyText="relapse.emptyText"
              sorter={sorter}
              mapper={historyMapper(names[0], onStartEdit, onView)}
            />
          )}
        </Paper>
        {editingDocument && (
          <BottomFormControls
            name={names[0]}
            editId={editingDocument.documentId}
            commitId={getLatestUnfinishedCommit(editingDocument).commitId}
          />
        )}
        <AlertDialog
          visible={this.state.alertVisible}
          alertMessage="relapse.infoTextPopup"
          cancelButtonText="dictionary.close"
          acceptButtonText="relapse.locateToStatus"
          onCancel={() => this.closeDialog()}
          onAccept={() => {
            window.location.href = '/documents/status';
          }}
          formatMessage={props => <FormattedMessage {...props} />}
          cancelButtonStyle={{ button: 'flat', primary: true }}
          acceptButtonStyle={{ button: 'raised', primary: true }}
        />
      </div>
    );
  }
}

Relapse.propTypes = {
  names: PropTypes.array,
  documents: PropTypes.array,
  viewId: PropTypes.object,
  onStartEdit: PropTypes.func.isRequired,
  onView: PropTypes.func,
  onCreate: PropTypes.func.isRequired,
  editingDocument: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
};

export default Relapse;
